import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useSearchNavigation } from '../../hooks/useSearchNavigation';
import { useLowConfidenceNavigation } from '../../hooks/useLowConfidenceNavigation';
import { useCurrentItemIndex } from '../../hooks/useCurrentItemIndex';
import { useTranscriptDataMaps } from '../../hooks/useTranscriptDataMaps';
import { useUtteranceSaving } from '../../hooks/useUtteranceSaving';
import { useTutorial } from '../../hooks/useTutorial';
import { useTutorialContext } from '../../contexts/TutorialContext';
import {
  EditorWord,
  TranscriptUtterance,
  Speaker,
  EditHistoryHook,
} from '../../types';
import LoadingSpinner from '../ui/LoadingSpinner';
import UndoRedoToolbar from '../tools/UndoRedoToolbar';
import {
  handleInput,
  handlePaste,
  handleKeyDown as handleInputKeyDown,
  InputHandlerCallbacks,
  cycleWordConfidenceAtIndex,
} from './inputHandlers';
import SearchNavigator from './SearchNavigator';
import LowConfidenceNavigator from './LowConfidenceNavigator';
import EditorUtterance from './EditorUtterance';
import SyncStatusIndicator from './SyncStatusIndicator';
import TutorialManager from '../tutorial/TutorialManager';
import { transcriptEditingTutorial } from '../tutorial/tutorials/TranscriptEditingTutorial';

interface EditorTranscriptProps {
  audioFileId: string;
  utterances: TranscriptUtterance[];
  setUtterances: React.Dispatch<React.SetStateAction<TranscriptUtterance[]>>;
  speakers: Speaker[];
  transcriptContainerRef: React.RefObject<HTMLDivElement | null>;
  loadingTranscript: boolean;
  playerCurrentTime: number | undefined;
  playerIsPlaying: boolean | undefined;
  editHistoryHook: EditHistoryHook;
  onPlayerPlaybackUpdate: (
    timestamp: number | undefined,
    playing: boolean | undefined
  ) => void;
  onSpeakerStatisticsRefresh: () => void;
  onUserInteraction: () => void;
}

const EditorTranscript: React.FC<EditorTranscriptProps> = ({
  audioFileId,
  utterances,
  setUtterances,
  speakers,
  transcriptContainerRef,
  loadingTranscript,
  playerCurrentTime,
  playerIsPlaying,
  editHistoryHook,
  onPlayerPlaybackUpdate,
  onSpeakerStatisticsRefresh,
  onUserInteraction,
}) => {
  // Combine related state to reduce re-renders
  const [playbackState, setPlaybackState] = useState({
    currentUtteranceIndex: -1,
    currentWordIndex: -1,
    isPlaying: false,
  });

  // Track when we need to sync DOM with state changes
  const [needsDOMSync, setNeedsDOMSync] = useState(false);

  // Create optimized lookup maps for O(1) access
  const { utteranceDataMap, wordDataMap } = useTranscriptDataMaps(utterances);

  // Handle navigation from search and low-confidence features
  const handleNavigateToWord = useCallback(
    (utteranceId: string, wordId: string) => {
      const utteranceData = utteranceDataMap.get(utteranceId);
      const wordData = wordDataMap.get(wordId);

      if (!utteranceData || !wordData) return;

      // Update playback state with the word's position
      setPlaybackState({
        currentUtteranceIndex: utteranceData.utteranceIndex,
        currentWordIndex: wordData.wordIndex,
        isPlaying: false,
      });

      // Notify the media player to update its position
      onPlayerPlaybackUpdate(wordData.startTime, false);

      // Scroll to the word with a small delay to ensure DOM is updated
      setTimeout(() => {
        const element = document.querySelector(`[data-word-id="${wordId}"]`);
        if (element) {
          element.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });
        }
      }, 50);
    },
    [utteranceDataMap, wordDataMap, onPlayerPlaybackUpdate]
  );

  const searchState = useSearchNavigation(
    utterances,
    handleNavigateToWord,
    audioFileId
  );
  const lowConfidenceState = useLowConfidenceNavigation(
    utterances,
    handleNavigateToWord,
    audioFileId
  );

  const getCurrentUtteranceIndex = useCurrentItemIndex(utterances);

  const currentUtterance =
    playbackState.currentUtteranceIndex >= 0 &&
    playbackState.currentUtteranceIndex < utterances.length
      ? utterances[playbackState.currentUtteranceIndex]
      : null;

  const { state: tutorialState } = useTutorialContext();

  // Scroll to first utterance when transcript tutorial starts
  useEffect(() => {
    if (
      tutorialState.currentTutorial === 'transcript-editing' &&
      tutorialState.currentStep === 0 &&
      transcriptContainerRef.current &&
      utterances.length > 0
    ) {
      const firstUtterance = transcriptContainerRef.current.querySelector(
        '[data-utterance-id]'
      );
      if (firstUtterance) {
        firstUtterance.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }
  }, [
    tutorialState.currentTutorial,
    tutorialState.currentStep,
    utterances.length,
    transcriptContainerRef,
  ]);

  // Trigger transcript editing tutorial when transcript is available
  // Only trigger if media player tutorial is not currently active and utterances are loaded
  useTutorial({
    tutorialId: 'transcript-editing',
    trigger:
      utterances.length > 0 &&
      !loadingTranscript &&
      tutorialState.currentTutorial !== 'media-player',
    delay: 1500,
  });

  const getCurrentWordIndex = useCurrentItemIndex(
    currentUtterance ? currentUtterance.transcriptWords : [],
    currentUtterance ? currentUtterance.startTime : 0,
    currentUtterance ? currentUtterance.endTime : 0
  );

  // Sync with external state
  // Don't depend on the functions getCurrentUtteranceIndex and
  // getCurrentWordIndex themselves, they can be recreated
  useEffect(() => {
    if (playerCurrentTime !== undefined) {
      const utteranceIndex = getCurrentUtteranceIndex(playerCurrentTime);
      if (utteranceIndex !== -1) {
        const wordIndex = getCurrentWordIndex(playerCurrentTime);

        setPlaybackState((prev) => ({
          ...prev,
          currentUtteranceIndex: utteranceIndex,
          currentWordIndex: wordIndex,
        }));
      }
    }

    if (playerIsPlaying !== undefined) {
      setPlaybackState((prev) => ({
        ...prev,
        isPlaying: playerIsPlaying,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerCurrentTime, playerIsPlaying]);

  // Async operations management
  const { syncStatus, queueOperation } = useUtteranceSaving({
    audioFileId,
    onAllOperationsComplete: () => {
      // Refresh edit history when all operations complete
      if (editHistoryHook) {
        editHistoryHook.refreshHistory();
      }
    },
    onOperationComplete: (operation) => {
      // Clear isNew and isModified flags after successful sync
      if (operation.type === 'utterance_words_edit') {
        const sentAt = operation.sentAt;

        setUtterances((prev) =>
          prev.map((utterance) =>
            utterance.id === operation.utteranceId
              ? {
                  ...utterance,
                  transcriptWords: utterance.transcriptWords.map(
                    (word: EditorWord) => {
                      // If word was modified after we sent the request, keep its flags
                      if (
                        sentAt &&
                        word.lastModifiedAt &&
                        word.lastModifiedAt > sentAt
                      ) {
                        // Word was modified after sending - keep flags
                        return word;
                      }

                      // Word wasn't modified since sending - clear flags
                      const {
                        isNew,
                        isModified,
                        lastModifiedAt,
                        ...cleanWord
                      } = word;
                      return cleanWord;
                    }
                  ),
                }
              : utterance
          )
        );
      }

      // Trigger speaker statistics refresh for operations that affect speakers
      if (
        operation.type === 'utterance_speaker_change' ||
        operation.type === 'utterance_split' ||
        operation.type === 'utterance_merge'
      ) {
        onSpeakerStatisticsRefresh?.();
      }
    },
  });

  // Input handler callback that updates state and queues operations
  const inputHandlerCallbacks: InputHandlerCallbacks = useMemo(
    () => ({
      onUtteranceSplit: (
        utteranceId: string,
        beforeWords: EditorWord[],
        newUtteranceId: string,
        afterWords: EditorWord[]
      ) => {
        // Find the first word ID of the second part
        const firstWordId = afterWords.find((word) => word.id)?.id;
        if (!firstWordId) {
          console.error('No valid first word found for split operation');
          return;
        }

        // Check if words were modified during split (word was split in half)
        const hasNewWords =
          beforeWords.some((word) => word.isNew) ||
          afterWords.some((word) => word.isNew);
        const hasModifiedWords =
          beforeWords.some((word) => word.isModified) ||
          afterWords.some((word) => word.isModified);

        if (hasNewWords || hasModifiedWords) {
          // First update the words for the original utterance with the split words
          const allWords = [...beforeWords, ...afterWords];
          queueOperation({
            type: 'utterance_words_edit',
            utteranceId,
            data: {
              newEditorWords: allWords,
            },
          });
        }

        // Then queue the split operation using the ID from input handler
        queueOperation({
          type: 'utterance_split',
          utteranceId,
          data: {
            newUtteranceId,
            firstWordId,
          },
        });

        // Trigger DOM sync after state updates
        setNeedsDOMSync(true);
      },
      onUtteranceMerge: (utteranceId: string, previousUtteranceId: string) => {
        // Queue backend sync operation
        queueOperation({
          type: 'utterance_merge',
          utteranceId: utteranceId,
          data: {
            targetUtteranceId: previousUtteranceId,
          },
        });

        // Trigger DOM sync after state updates
        setNeedsDOMSync(true);
      },
      onWordsEdit: (utteranceId: string, newEditorWords: EditorWord[]) => {
        // Update shared state immediately with word array
        setUtterances((prev) =>
          prev.map((p) =>
            p.id === utteranceId ? { ...p, transcriptWords: newEditorWords } : p
          )
        );

        // Queue backend sync if there were changes
        queueOperation({
          type: 'utterance_words_edit',
          utteranceId,
          data: {
            newEditorWords,
          },
        });
      },
    }),
    [setUtterances, queueOperation, setNeedsDOMSync]
  );

  // Optimistic speaker change
  const handleSpeakerChange = useCallback(
    (utteranceId: string, speakerId: string) => {
      // Update UI immediately (optimistic)
      setUtterances((prev) =>
        prev.map((utterance) =>
          utterance.id === utteranceId
            ? {
                ...utterance,
                speakerId: speakerId,
              }
            : utterance
        )
      );

      // Queue backend sync (non-blocking)
      queueOperation({
        type: 'utterance_speaker_change',
        utteranceId,
        data: {
          newSpeakerId: speakerId,
        },
      });
    },
    [setUtterances, queueOperation]
  );

  // Create wrapped handlers that use the input handler functions
  const handleInputWrapper = useCallback(
    (e: React.FormEvent<HTMLDivElement>, utteranceId: string) => {
      handleInput(
        e,
        utteranceId,
        utteranceDataMap,
        utterances,
        inputHandlerCallbacks
      );
    },
    [utterances, utteranceDataMap, inputHandlerCallbacks]
  );

  const handleKeyDownWrapper = useCallback(
    (e: React.KeyboardEvent, utteranceId: string) => {
      handleInputKeyDown(
        e,
        utteranceId,
        utteranceDataMap,
        utterances,
        setUtterances,
        inputHandlerCallbacks
      );
    },
    [utterances, setUtterances, utteranceDataMap, inputHandlerCallbacks]
  );

  // Handle DOM sync after state changes
  useEffect(() => {
    if (needsDOMSync) {
      // Reset flag after DOM updates
      const timer = setTimeout(() => setNeedsDOMSync(false), 100);
      return () => clearTimeout(timer);
    }
  }, [needsDOMSync]);

  const handleUtteranceClick = useCallback(
    (event: React.MouseEvent, utteranceId: string) => {
      const utteranceData = utteranceDataMap.get(utteranceId);
      if (!utteranceData) return;

      const utteranceIndex = utteranceData.utteranceIndex;
      let wordIndex = 0;
      let startTime = utteranceData.startTime;

      const target = event.target as HTMLElement;
      const wordElement = target.closest('[data-word-id]');
      if (wordElement) {
        const wordId = wordElement.getAttribute('data-word-id');
        if (wordId) {
          const wordData = wordDataMap.get(wordId);
          if (wordData) {
            wordIndex = wordData.wordIndex;
            startTime = wordData.startTime;
          }
        }
      }

      // Single state update for immediate response
      setPlaybackState({
        currentUtteranceIndex: utteranceIndex,
        currentWordIndex: wordIndex,
        isPlaying: false,
      });
      onPlayerPlaybackUpdate(startTime, false);
    },
    [utteranceDataMap, wordDataMap, onPlayerPlaybackUpdate]
  );

  // Handle keyboard shortcuts in edit mode
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      const target = event.target as HTMLElement;

      // Only handle shortcuts when focused inside contentEditable elements
      if (target.closest('[contenteditable="true"]')) {
        // Handle Tab key to toggle play/pause
        if (event.key === 'Tab') {
          event.preventDefault();
          const isPlaying = playbackState.isPlaying;
          setPlaybackState((prev) => ({
            ...prev,
            isPlaying: !isPlaying,
          }));
          onPlayerPlaybackUpdate(undefined, !isPlaying);
        }
        // Handle Cmd/Ctrl+I to cycle word confidence
        else if (event.key === 'i' && (event.metaKey || event.ctrlKey)) {
          event.preventDefault();

          // Only cycle if we have a current word
          if (
            playbackState.currentUtteranceIndex >= 0 &&
            playbackState.currentWordIndex >= 0
          ) {
            const currentUtterance =
              utterances[playbackState.currentUtteranceIndex];
            if (currentUtterance) {
              cycleWordConfidenceAtIndex(
                currentUtterance,
                playbackState.currentWordIndex,
                inputHandlerCallbacks
              );
            }
          }
        }
      }
    },
    [playbackState, utterances, inputHandlerCallbacks, onPlayerPlaybackUpdate]
  );

  // Add event listeners for keyboard, word clicks, and user interactions
  useEffect(() => {
    // Add listeners to the transcript container
    const container = transcriptContainerRef.current;
    if (container) {
      container.addEventListener('keydown', handleKeyDown);
    }

    return () => {
      if (container) {
        container.removeEventListener('keydown', handleKeyDown);
      }
    };
  }, [transcriptContainerRef, handleKeyDown]);

  // Don't use useMemo here - let React's reconciliation handle it
  const renderedUtterances = utterances.map(
    (utterance: TranscriptUtterance, index: number) => {
      const isCurrentUtterance = index === playbackState.currentUtteranceIndex;
      const currentWordIndex = isCurrentUtterance
        ? playbackState.currentWordIndex
        : -1;

      return (
        <EditorUtterance
          key={utterance.id}
          utterance={utterance}
          speakers={speakers}
          needsDOMSync={needsDOMSync}
          isCurrentUtterance={isCurrentUtterance}
          currentWordIndex={currentWordIndex}
          isPlaying={playbackState.isPlaying}
          searchState={searchState}
          onSpeakerChange={handleSpeakerChange}
          onUtteranceClick={handleUtteranceClick}
          onKeyDown={handleKeyDownWrapper}
          onInput={handleInputWrapper}
          onPaste={handlePaste}
        />
      );
    }
  );

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-gray-900 !min-h-0 !bg-transparent">
      <header className="flex-shrink-0 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 flex-shrink-0">
            Transcript
          </h3>

          <div className="flex flex-wrap items-center gap-3 ml-auto">
            <SearchNavigator
              query={searchState.query}
              isActive={searchState.isActive}
              totalCount={searchState.matches.length}
              currentIndex={searchState.currentIndex}
              onQueryChange={searchState.setSearchQuery}
              onQueryClear={searchState.clearSearch}
              onNextMatch={searchState.goToNextMatch}
              onPrevMatch={searchState.goToPrevMatch}
            />

            <LowConfidenceNavigator
              isActive={lowConfidenceState.isActive}
              totalCount={lowConfidenceState.lowConfidenceWords.length}
              currentIndex={lowConfidenceState.currentIndex}
              onNext={lowConfidenceState.goToNext}
              onPrev={lowConfidenceState.goToPrev}
            />

            {editHistoryHook && (
              <UndoRedoToolbar editHistoryHook={editHistoryHook} />
            )}
            <SyncStatusIndicator
              syncStatus={editHistoryHook?.isLoading ? 'pending' : syncStatus}
            />
          </div>
        </div>
      </header>

      <div
        ref={transcriptContainerRef}
        className="flex-1 min-h-0 overflow-y-auto"
        onMouseDown={onUserInteraction}
        onKeyDown={onUserInteraction}
        onWheel={onUserInteraction}
        onTouchStart={onUserInteraction}
      >
        {loadingTranscript ? (
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <LoadingSpinner size="lg" />
              <div className="mt-4 text-gray-600 dark:text-gray-400">
                Loading transcript...
              </div>
            </div>
          </div>
        ) : utterances.length > 0 ? (
          <div className="space-y-4 pr-4 pb-48">{renderedUtterances}</div>
        ) : (
          <div className="text-center text-gray-500 dark:text-gray-400 py-8">
            No transcript utterances found
          </div>
        )}
      </div>

      <TutorialManager tutorials={[transcriptEditingTutorial]} />
    </div>
  );
};

export default EditorTranscript;
